import Button from "@/components/ui/Button";
import Dropdown from "@/components/ui/DropDown";
import TextField from "@/components/ui/Textfield";
import { IoIosClose } from "react-icons/io";
import { useEffect, useState } from "react";
import { PlusIcon } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import Spinner from "@/components/ui/Spinner";
import useGlobalStore from "@/store/globalStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CampaignData } from "@/pages/dashboard/store/dashboardStore";
import useCampaignStore from "../store/campaignStore";

const namingOptions = [
  { title: "Client", value: "Client" },
  { title: "Language", value: "Language" },
  { title: "Campaign", value: "Campaign" },
  { title: "Status", value: "Status" },
  { title: "Platform", value: "Platform" },
  { title: "Ad Type", value: "Ad Type" },
  { title: "Product", value: "Product" },
];

const CreateCampaign = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isCampaignCreating, setIsCampaignCreating] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    selectedClient: "",
    teamMembers: [] as string[],
    campaignName: "",
    projectCode: "",
    budget: "",
    languages: [] as string[],
    fileNamingConvention: [] as string[],
  });

  const handleChange = (key: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleArrayChange = (key: keyof typeof formData, value: string) => {
    if (!formData[key].includes(value)) {
      setFormData((prev) => ({
        ...prev,
        [key]: [...(prev[key] as string[]), value],
      }));
    }
  };

  const handleRemoveArrayItem = (key: keyof typeof formData, index: number) => {
    setFormData((prev) => ({
      ...prev,
      [key]: (prev[key] as string[]).filter((_, i) => i !== index),
    }));
  };

  const {
    getClientOptions,
    getTeamOptions,
    getLanguageOptions,
    clientOptions,
    teamOptions,
    languageOptions,
    createCampaign,
    campaign,
    updateCampaign,
  } = useCampaignStore(
    useShallow((state) => ({
      getClientOptions: state.getClientOptions,
      getTeamOptions: state.getTeamOptions,
      getLanguageOptions: state.getLanguageOptions,
      clientOptions: state.clientOptions,
      teamOptions: state.teamOptions,
      languageOptions: state.languageOptions,
      createCampaign: state.createCampaign,
      campaign: state.campaign,
      updateCampaign: state.updateCampaign,
    })),
  );

  const { setError, showSuccess } = useGlobalStore();

  const fetchOptions = async () => {
    try {
      await getClientOptions();
      await getTeamOptions();
      await getLanguageOptions();
    } catch (error) {
      console.log(error);
      setError(error.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      selectedClient,
      teamMembers,
      campaignName,
      projectCode,
      budget,
      languages,
      fileNamingConvention,
    } = formData;

    if (!selectedClient.trim()) return setError("Client is required.");
    if (teamMembers.length === 0)
      return setError("At least one team member is required.");
    if (!campaignName.trim()) return setError("Campaign name is required.");
    if (languages.length === 0)
      return setError("Select at least one language.");
    if (fileNamingConvention.length === 0)
      return setError("File naming convention is required.");

    let numericBudget;
    if (budget) {
      numericBudget = parseFloat(
        budget
          ?.toString()
          .replace(/[$,\s]/g, "")
          .replace(/[^\d.]/g, ""),
      );
      if (isNaN(numericBudget))
        return setError("Budget must be a valid number.");
    }

    try {
      setIsCampaignCreating(true);

      let campaign_data: CampaignData;

      if (searchParams.get("id")) {
        campaign_data = await updateCampaign(
          {
            name: campaignName,
            budget: numericBudget,
            client_team_id: selectedClient,
            team_ids: teamMembers,
            language_ids: languages,
            file_naming_convention: fileNamingConvention.join("_"),
          },
          searchParams.get("id"),
        );
        showSuccess("Campaign updated Successfully");
      } else {
        campaign_data = await createCampaign({
          name: campaignName,
          project_code: projectCode,
          budget: numericBudget,
          client_team_id: selectedClient,
          team_ids: teamMembers,
          language_ids: languages,
          file_naming_convention: fileNamingConvention.join("_"),
        });
        showSuccess("Campaign created Successfully");
      }

      navigate(`/campaign/flight?id=${campaign_data.campaign.id}`);
    } catch (err: any) {
      setError(err?.message || "Failed to create campaign.");
    } finally {
      setIsCampaignCreating(false);
    }
  };

  const fetchUpdateData = () => {
    if (searchParams.get("id")) {
      setFormData({
        campaignName: campaign.campaign.name,
        budget: campaign.campaign.budget?.toString(),
        fileNamingConvention:
          campaign.campaign.file_naming_convention.split("_"),
        projectCode: campaign.campaign.project_code,
        languages: campaign.languages.map((e) => e.language_id),
        teamMembers: campaign.teams
          .filter((e) => e.team_type != "client")
          .map((e) => e.team_id),
        selectedClient: campaign.teams.filter((e) => e.team_type == "client")[0]
          .team_id,
      });
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchUpdateData();
  }, []);

  if (isLoading) {
    return <Spinner className="flex justify-center" />;
  }

  return (
    <div className="p-2 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-semibold mb-8">
        {searchParams.get("id") ? "Update Campaign" : "Create Campaign"}
      </h1>

      <section>
        <h2 className="text-lg font-semibold mb-3">Campaign Settings</h2>

        <hr className="mb-6 border-gray-300" />

        <div className="mb-6">
          <label className="block font-semibold mb-1">Client</label>
          <div className="flex items-center">
            <Dropdown
              options={clientOptions}
              placeholder="Select client..."
              className="w-1/2"
              selected={formData.selectedClient}
              setSelected={(val) => handleChange("selectedClient", val)}
            />
            {/* <Button type="secondary" className="ml-5" trailingIcon={<PlusIcon />}>
                <span>Create New Client</span>
              </Button> */}
          </div>
        </div>

        <div className="mb-6">
          <label className="block font-semibold mb-1">Team</label>
          <p className="text-gray-500 mb-2 text-sm">
            Select team members from the dropdown to add them to your campaign,
            or invite someone new.
          </p>
          <div className="flex items-center">
            <Dropdown
              options={teamOptions.filter(
                (option) => !formData.teamMembers.includes(option.value),
              )}
              placeholder="Add team members..."
              className="w-1/2"
              selected={""}
              setSelected={(val) =>
                handleArrayChange("teamMembers", val.toString())
              }
            />
            {/* <Button type="secondary" className="ml-6" trailingIcon={<PlusIcon />}>
                <span>Invite Someone New</span>
              </Button> */}
          </div>
          <div className="mt-3 flex space-x-2">
            {formData.teamMembers.map((member, index) => (
              <div key={member} className="relative">
                <button
                  onClick={() => handleRemoveArrayItem("teamMembers", index)}
                  className="absolute -top-2 -right-2 bg-white text-pink-500 border border-pink-500 rounded-full w-5 h-5 text-xs flex items-center justify-center hover:bg-pink-100"
                  aria-label="Remove member"
                >
                  <IoIosClose />
                </button>

                <div className="bg-pink-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-base font-bold">
                  {(teamOptions.find((e) => e.value === member)?.title || "")
                    .split(" ")
                    .slice(0, 2)
                    .map((word) => word[0])
                    .join("")
                    .toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex space-x-6 mb-6">
          <div className="flex-1">
            <label className="block font-semibold mb-1">Campaign Name</label>
            <TextField
              type="text"
              placeholder="Enter campaign title"
              value={formData.campaignName}
              onChange={(e) => handleChange("campaignName", e.target.value)}
              className="w-full"
            />
          </div>
          <div className="w-48">
            <label className="block font-semibold mb-1">Project Code</label>
            <TextField
              type="text"
              placeholder="Enter code (optional)"
              value={formData.projectCode}
              onChange={(e) => handleChange("projectCode", e.target.value)}
              className="w-full"
              disabled={searchParams.get("id") ? true : false}
            />
          </div>
          <div className="w-48">
            <label className="block font-semibold mb-1">Budget</label>
            <TextField
              type="text"
              placeholder="Enter value (optional)"
              value={formData.budget}
              onChange={(e) => handleChange("budget", e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block font-semibold mb-1">Languages</label>
          <p className="text-gray-500 mb-2 text-sm">
            Select a language from the dropdown menu to add it to your campaign.
          </p>
          <Dropdown
            options={languageOptions.filter(
              (option) => !formData.languages.includes(option.value),
            )}
            placeholder="Add language..."
            className="w-full"
            selected={""}
            setSelected={(val) =>
              handleArrayChange("languages", val.toString())
            }
          />
          <div className="mt-2 flex space-x-2">
            {formData.languages.map((lang, index) => (
              <div
                key={lang}
                className="border border-gray-300 text-gray-700 rounded-full pl-3 pr-2 py-1 text-sm flex items-center"
              >
                {languageOptions.find((e) => e.value === lang)?.title || lang}
                <IoIosClose
                  className="ml-1 cursor-pointer"
                  fontSize={20}
                  onClick={() => {
                    handleRemoveArrayItem("languages", index);
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block font-semibold mb-1">
            File Naming Convention
          </label>
          <p className="text-gray-500 mb-2 text-sm">
            All assets will use this naming convention upon export. Below is the
            default naming convention – drag or delete fields to reorder to your
            desired convention.
          </p>
          <Dropdown
            options={namingOptions.filter(
              (option) => !formData.fileNamingConvention.includes(option.value),
            )}
            placeholder="Add file naming Options ..."
            className="w-full"
            selected={""}
            setSelected={(val) =>
              handleArrayChange("fileNamingConvention", val.toString())
            }
          />
          <div className="mt-2 flex space-x-2">
            {formData.fileNamingConvention.map((lang, index) => (
              <div
                key={lang}
                className="border border-gray-300 text-gray-700 rounded-full pl-3 pr-2 py-1 text-sm flex items-center"
              >
                {namingOptions.filter((e) => e.value == lang)[0].title}{" "}
                <IoIosClose
                  className="ml-1 cursor-pointer"
                  fontSize={20}
                  onClick={() => {
                    handleRemoveArrayItem("fileNamingConvention", index);
                  }}
                />
              </div>
            ))}
          </div>
          {formData.fileNamingConvention.length !== 0 && (
            <p className="mt-4">
              This convention would look like this:{" "}
              <span className="text-pink-500 font-semibold">
                {formData.fileNamingConvention.join("_")}.jpg
              </span>
            </p>
          )}
        </div>

        <hr className="border-gray-300 mb-6" />

        <Button
          type="primary"
          trailingIcon={<PlusIcon />}
          onClick={handleSubmit}
          isLoading={isCampaignCreating}
        >
          {searchParams.get("id") ? "Update Campaign" : "Create Campaign"}
        </Button>
      </section>
    </div>
  );
};

export default CreateCampaign;
